// The presentational Lobby route (session-handoff task 3.6; UI.md §6; MATCH-LIFECYCLE
// §2), composing the ported `core` components over the store's `lobby` slice. Before a
// session exists (`store.lobby === null`) it offers create/join over a match code +
// display name; once a `LobbyState` lands in the store it renders the wordmark,
// match-code entry, roster + bot-fill row, host-editable match-config strip, and the
// join/start CTA — config editing and Start are offered only to the host
// (`LobbyState.isHost`).
//
// It constructs **no** `LobbyClient` and **no** `Connection` of its own (client-lobby
// "The lobby CTAs hand off to the live match session"): the composition root owns the
// one `LobbyClient` + roster poll loop and injects a `SessionLobbyController`; every
// CTA calls a hand-off entry point (`create`/`join`/`ready`/`start`/`cancel`/`retry`)
// and the screen reads the phase from the controller to render its bounded
// `connecting` surface. Navigation into the match is driven by the root's first
// `Welcome`, never a CTA click.

import { useState } from 'react';
import type { LobbyConfig, RosterEntry } from '../../net/lobby-http';
import type { SessionLobbyController } from '../../app/SessionController';
import { useClientStore } from '../../state/store';
import { ENTRY_PAGE_FRAME } from '../page-frame';
import { Button } from '../components/core/Button';
import { Badge, Chip } from '../components/core/Chip';
import { Panel } from '../components/core/Panel';

export interface LobbyProps {
  /** The composition root's hand-off seam (design DD5). Undefined in a bare
   * `AppRoutes`/`MemoryRouter` test mount, where the inert default renders create/join
   * over a no-op hand-off (the pre-wiring "not owned by a root yet" reality). */
  controller?: SessionLobbyController;
}

const DEFAULT_CONFIG: LobbyConfig = {
  durationMinutes: 30,
  seasonArc: 'summer → winter',
  colonyCount: 24,
  beekeeperEnabled: true,
};

/** Inert fallback controller (never opens/sends): used only when a mount supplies no
 * `controller` at all (a bare `AppRoutes` test mount, before the composition root owns
 * it). Matches the `Colony`/`Match` inert-default precedent. */
const INERT_CONTROLLER: SessionLobbyController = {
  phase: 'lobby',
  connectingStalled: false,
  create: async () => undefined,
  join: async () => undefined,
  claim: async () => undefined,
  ready: async () => undefined,
  start: async () => undefined,
  cancel: async () => undefined,
  retry: () => undefined,
};

function ReadyBadge({ status }: { status: RosterEntry['status'] }) {
  return status === 'ready' ? (
    <Badge tone="success">Ready</Badge>
  ) : (
    <Badge tone="neutral">Picking name…</Badge>
  );
}

export function Lobby({ controller = INERT_CONTROLLER }: LobbyProps = {}) {
  const lobby = useClientStore((s) => s.lobby);

  const [name, setName] = useState('');
  const [matchCode, setMatchCode] = useState('');
  const [config, setConfig] = useState<LobbyConfig>(() => lobby?.config ?? DEFAULT_CONFIG);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const phase = controller.phase;
  const stalled = controller.connectingStalled;
  const connecting = phase === 'connecting';
  const isHost = lobby?.isHost ?? false;

  async function run(op: () => Promise<void>): Promise<void> {
    setError(null);
    setBusy(true);
    try {
      await op();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      data-testid="lobby-screen"
      // The shared entry-surface frame (`ui/page-frame.ts`). Without it the lobby's panels and
      // fields stretch to whatever width the browser happens to be and sit flush against its
      // edges — `#root` fills the viewport and nothing else here bounds them. Both branches
      // below inherit it.
      style={{ ...ENTRY_PAGE_FRAME, display: 'flex', flexDirection: 'column', gap: 16 }}
    >
      <header style={{ textAlign: 'center' }}>
        <h1 style={{ fontFamily: 'var(--font-display)', margin: 0 }}>Waggle</h1>
        <p style={{ color: 'var(--text-muted)', margin: 0 }}>
          Don’t command your bees. Convince them.
        </p>
      </header>

      <Panel title="Match code" sunken>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            id="match-code"
            name="match-code"
            type="text"
            placeholder="e.g. HONEY-42"
            value={lobby !== null ? lobby.matchCode : matchCode}
            readOnly={lobby !== null}
            onChange={(e) => setMatchCode(e.target.value)}
          />
          <Button
            variant="secondary"
            onClick={() => {
              if (lobby !== null) {
                return;
              }
              void navigator.clipboard
                ?.readText()
                .then(setMatchCode)
                .catch(() => undefined);
            }}
          >
            Paste
          </Button>
        </div>
      </Panel>

      {lobby === null ? (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label htmlFor="player-name">Name</label>
            <input id="player-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          {error !== null && <p role="alert">{error}</p>}
          <div style={{ display: 'flex', gap: 10 }}>
            <Button onClick={() => void run(() => controller.create(name, config))} disabled={busy || name.trim() === ''}>
              Create match
            </Button>
            <Button
              variant="secondary"
              onClick={() => void run(() => controller.join(matchCode, name))}
              disabled={busy || name.trim() === '' || matchCode.trim() === ''}
            >
              Join match
            </Button>
          </div>
        </>
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span
              style={{
                fontWeight: 800,
                fontSize: 11,
                textTransform: 'uppercase',
                letterSpacing: 'var(--tracking-caps)',
                color: 'var(--text-muted)',
              }}
            >
              In the lobby · {lobby.players.length} of {lobby.capacity}
            </span>
            {lobby.players.map((p) => (
              <div
                key={p.name}
                data-testid="roster-row"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  background: 'var(--surface-card)',
                  border: '1px solid var(--line)',
                  borderRadius: 14,
                  boxShadow: 'var(--shadow-card)',
                  padding: '10px 14px',
                  minHeight: 56,
                }}
              >
                <div>
                  <b>
                    {p.name}
                    {p.isHost ? ' 👑' : ''}
                  </b>
                  <small style={{ display: 'flex', gap: 4, color: 'var(--text-muted)' }}>
                    <span>{p.colonyName ?? '—'}</span>
                    {p.isHost && <span>· host</span>}
                  </small>
                </div>
                <div style={{ marginLeft: 'auto' }}>
                  <ReadyBadge status={p.status} />
                </div>
              </div>
            ))}
            <div
              data-testid="bot-fill-row"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                background: 'var(--surface-card)',
                border: '1px solid var(--line)',
                borderRadius: 14,
                boxShadow: 'var(--shadow-card)',
                padding: '10px 14px',
                minHeight: 56,
                opacity: 0.75,
              }}
            >
              + {lobby.botFill} bot colonies · mixed skill
            </div>
          </div>

          <Panel title="Match config">
            {lobby.isHost ? (
              <div data-testid="config-editable" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <label htmlFor="cfg-duration">Duration (min)</label>
                <input
                  id="cfg-duration"
                  type="number"
                  value={config.durationMinutes}
                  onChange={(e) => setConfig({ ...config, durationMinutes: Number(e.target.value) })}
                />
                <label htmlFor="cfg-colonies">Colonies</label>
                <input
                  id="cfg-colonies"
                  type="number"
                  value={config.colonyCount}
                  onChange={(e) => setConfig({ ...config, colonyCount: Number(e.target.value) })}
                />
                <Chip
                  selected={config.beekeeperEnabled}
                  onClick={() => setConfig({ ...config, beekeeperEnabled: !config.beekeeperEnabled })}
                >
                  Beekeeper {config.beekeeperEnabled ? 'on' : 'off'}
                </Chip>
              </div>
            ) : (
              <div
                data-testid="config-readonly"
                style={{ display: 'flex', gap: 10, justifyContent: 'space-between' }}
              >
                <span>
                  {lobby.config.durationMinutes} min · {lobby.config.seasonArc}
                </span>
                <span>{lobby.config.colonyCount} colonies</span>
                <span>Beekeeper {lobby.config.beekeeperEnabled ? 'on' : 'off'}</span>
              </div>
            )}
          </Panel>

          {error !== null && <p role="alert">{error}</p>}

          {connecting ? (
            <div
              data-testid="connecting-indicator"
              style={{ display: 'flex', alignItems: 'center', gap: 10 }}
            >
              {stalled ? (
                <>
                  <span>Still connecting…</span>
                  <button type="button" data-testid="connecting-retry" onClick={() => controller.retry()}>
                    Retry
                  </button>
                </>
              ) : isHost ? (
                <span>Starting the match…</span>
              ) : (
                <>
                  <span>Joining the meadow…</span>
                  <button
                    type="button"
                    data-testid="connecting-cancel"
                    onClick={() => void run(() => controller.cancel())}
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>
          ) : lobby.isHost ? (
            <Button onClick={() => void run(() => controller.start())} disabled={busy}>
              Start
            </Button>
          ) : (
            <Button onClick={() => void run(() => controller.ready(true))} disabled={busy}>
              Join the meadow
            </Button>
          )}
        </>
      )}
    </div>
  );
}
